import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  formatMoney, formatPoints, formatRating, nomeDoPeriodo, periodoAnterior,
  type ResumoPessoal,
} from "@nhomi/core";
import { useSession } from "@/lib/auth";
import { pedirResumo, watchResumo, watchResumoPessoal, type Resumo as ResumoDoc } from "@/data/resumos";
import { Spinner } from "@/ui/primitives";
import { StoragePhoto } from "@/ui/StoragePhoto";
import { desenharPodio, fraseDoPodio } from "./Temporada";

/* ============================================================
   Resumo do mês / retrospectiva do ano, em cartões

   Um cartão por vez, como stories: toca à direita para avançar, à
   esquerda para voltar. Dois roteiros — o do grupo e o seu — e o do
   grupo termina no pódio, com a imagem para o WhatsApp.

   O resumo é montado pelo servidor quando o período fecha. Se a pessoa
   chega antes (setembro, que fechou antes de isto existir), a tela pede
   e espera: a montagem leva alguns segundos e acontece uma vez só.
   ============================================================ */

type Aba = "grupo" | "eu";
interface Cartao { id: string; corpo: ReactNode }

export function Resumo() {
  const { cid = "", periodo = "" } = useParams();
  const { user } = useSession();
  const [grupo, setGrupo] = useState<ResumoDoc | null | undefined>(undefined);
  const [eu, setEu] = useState<ResumoPessoal | null | undefined>(undefined);
  const [aba, setAba] = useState<Aba>("grupo");
  const [i, setI] = useState(0);
  const [pedindo, setPedindo] = useState(false);
  const [erro, setErro] = useState("");
  const pediu = useRef(false);

  useEffect(() => watchResumo(cid, periodo, setGrupo), [cid, periodo]);
  useEffect(() => (user ? watchResumoPessoal(cid, periodo, user.uid, setEu) : undefined), [cid, periodo, user]);

  // Ainda não montado: pede uma vez e espera o documento chegar.
  useEffect(() => {
    if (grupo !== null || pediu.current) return;
    pediu.current = true;
    setPedindo(true);
    pedirResumo(cid, periodo)
      .catch((e: { message?: string }) => setErro(e.message || "Não deu pra montar o resumo agora."))
      .finally(() => setPedindo(false));
  }, [grupo, cid, periodo]);

  const ano = periodo.length === 4;
  const nome = nomeDoPeriodo(periodo, new Date().getFullYear());
  const titulo = ano ? `Retrospectiva ${periodo}` : `Resumo de ${nome}`;

  const cartoes: Cartao[] = useMemo(() => {
    if (!grupo) return [];
    return aba === "grupo" ? cartoesDoGrupo(grupo, cid, ano, nome) : cartoesPessoais(eu ?? null, ano, nome, periodo);
  }, [grupo, eu, aba, cid, ano, nome, periodo]);

  useEffect(() => setI(0), [aba]);

  if (grupo === undefined || (grupo === null && (pedindo || !erro))) {
    return (
      <main className="resumo">
        <div className="resumo-carregando"><Spinner /><p>{pedindo ? "Montando o resumo…" : "Carregando…"}</p></div>
      </main>
    );
  }
  if (!grupo) {
    return (
      <main className="resumo">
        <Link className="resumo-fechar" to={`/c/${cid}/recap`} aria-label="Fechar">×</Link>
        <div className="resumo-carregando"><p>{erro}</p></div>
      </main>
    );
  }

  const atual = cartoes[Math.min(i, cartoes.length - 1)];
  return (
    <main className="resumo">
      <div className="resumo-barras">
        {cartoes.map((c, k) => <span key={c.id} className={k <= i ? "on" : ""} />)}
      </div>
      <div className="resumo-topo">
        <div className="resumo-abas" role="tablist">
          <button role="tab" aria-selected={aba === "grupo"} className={aba === "grupo" ? "on" : ""} onClick={() => setAba("grupo")}>Do grupo</button>
          <button role="tab" aria-selected={aba === "eu"} className={aba === "eu" ? "on" : ""} onClick={() => setAba("eu")}>Seu</button>
        </div>
        <Link className="resumo-fechar" to={`/c/${cid}/recap`} aria-label="Fechar">×</Link>
      </div>
      <div className="resumo-rotulo">{grupo.nomeDoDesafio ?? ""} · {titulo}</div>

      <section className="resumo-cartao" aria-live="polite">{atual?.corpo}</section>

      <button className="resumo-toque esq" aria-label="Cartão anterior" onClick={() => setI((v) => Math.max(0, v - 1))} />
      <button className="resumo-toque dir" aria-label="Próximo cartão" onClick={() => setI((v) => Math.min(cartoes.length - 1, v + 1))} />
    </main>
  );
}

/* ---------- os cartões ---------- */

const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const primeiro = (n: string) => n.split(" ")[0] || n;

function Grande({ n, rotulo }: { n: string | number; rotulo: string }) {
  return <div className="resumo-num"><b>{n}</b><span>{rotulo}</span></div>;
}

function cartoesDoGrupo(r: ResumoDoc, cid: string, ano: boolean, nome: string): Cartao[] {
  const l: Cartao[] = [];
  l.push({ id: "capa", corpo: (
    <>
      <h1>{ano ? `O ano de ${r.periodo}` : `${nome[0]!.toUpperCase()}${nome.slice(1)}`} em números</h1>
      <div className="resumo-grade">
        <Grande n={r.pratos} rotulo={r.pratos === 1 ? "prato" : "pratos"} />
        <Grande n={r.pessoas} rotulo={r.pessoas === 1 ? "pessoa postou" : "pessoas postaram"} />
        <Grande n={r.diasAtivos} rotulo="dias com prato" />
        <Grande n={`${pct(r.cozinhados, r.pratos)}%`} rotulo="feito em casa" />
      </div>
    </>
  ) });
  if (r.ingrediente) l.push({ id: "ingrediente", corpo: (
    <>
      <p className="resumo-pre">O ingrediente {ano ? "do ano" : "do mês"}</p>
      <h1 className="resumo-destaque">{r.ingrediente.nome}</h1>
      <p>Apareceu em {r.ingrediente.pratos} dos {r.ingrediente.comEstimativa} pratos com estimativa de calorias.</p>
    </>
  ) });
  if (r.cozinhas.length) l.push({ id: "cozinhas", corpo: (
    <>
      <p className="resumo-pre">As cozinhas</p>
      <ol className="resumo-lista">
        {r.cozinhas.map((c) => <li key={c.id}><span>{c.emoji} {c.label}</span><b>{c.n}</b></li>)}
      </ol>
    </>
  ) });
  if (r.lugar) l.push({ id: "lugar", corpo: (
    <>
      <p className="resumo-pre">O lugar favorito</p>
      <h1 className="resumo-destaque">📍 {r.lugar.nome}</h1>
      <p>{r.lugar.visitas} visitas.</p>
    </>
  ) });
  if (r.curiosidades.length) l.push({ id: "curiosidades", corpo: (
    <>
      <p className="resumo-pre">Curiosidades</p>
      <ul className="resumo-frases">{r.curiosidades.map((f) => <li key={f}>{f}</li>)}</ul>
    </>
  ) });
  if (r.pratoDoPeriodo) {
    const p = r.pratoDoPeriodo;
    l.push({ id: "prato", corpo: (
      <>
        <p className="resumo-pre">O prato {ano ? "do ano" : "do mês"}</p>
        {p.thumbPath && <div className="resumo-foto"><StoragePhoto path={p.thumbPath} alt="" ratio="1 / 1" /></div>}
        <h2>{p.title || "Sem título"}</h2>
        <p>{primeiro(p.authorName)} · ⭐ {formatRating(p.nota)} ({p.notas} notas)</p>
        <Link className="resumo-link" to={`/c/${cid}/p/${p.id}`}>Ver o prato</Link>
      </>
    ) });
  }
  if (r.missoes.total) l.push({ id: "missoes", corpo: (
    <>
      <p className="resumo-pre">Missões</p>
      <h1 className="resumo-destaque">{r.missoes.total} {r.missoes.total === 1 ? "missão fechada" : "missões fechadas"}</h1>
      {ano && r.missoes.preferida && (
        <p>A preferida foi a {r.missoes.preferida}, {r.missoes.porMissao[0]?.n} vezes.</p>
      )}
      <ol className="resumo-lista">
        {r.missoes.porMissao.map((m) => <li key={m.nome}><span>{m.emoji} {m.nome}</span><b>{m.n}×</b></li>)}
      </ol>
    </>
  ) });
  l.push({ id: "podio", corpo: <PodioDoResumo r={r} ano={ano} nome={nome} /> });
  return l;
}

function PodioDoResumo({ r, ano, nome }: { r: ResumoDoc; ano: boolean; nome: string }) {
  const [aviso, setAviso] = useState("");
  const max = r.podio[0]?.pontos ?? 0;
  const campeoes = r.podio.filter((l) => l.pontos === max && max > 0).map((l) => l.uid);
  async function compartilhar() {
    const blob = await desenharPodio({
      desafio: r.nomeDoDesafio ?? "Nhomi", intervalo: ano ? r.periodo : nome,
      frase: fraseDoPodio({ placar: r.podio, campeoes }, ano ? "o ano" : "o mês"), placar: r.podio, campeoes,
    });
    const arquivo = new File([blob], `nhomi-${r.periodo}.png`, { type: "image/png" });
    try {
      if (navigator.canShare?.({ files: [arquivo] })) { await navigator.share({ files: [arquivo] }); return; }
    } catch (e) { if ((e as Error).name === "AbortError") return; }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = arquivo.name; a.click();
    setAviso("Imagem baixada. É só mandar no grupo.");
  }
  return (
    <>
      <p className="resumo-pre">O pódio {ano ? "do ano" : "do mês"}</p>
      {r.podio.length ? (
        <ol className="resumo-lista podio">
          {r.podio.map((l, k) => (
            <li key={l.uid}><span>{["🥇", "🥈", "🥉"][k]} {l.name}</span><b>{formatPoints(l.pontos)} pts</b></li>
          ))}
        </ol>
      ) : <p>Ninguém pontuou neste período.</p>}
      {r.podio.length > 0 && (
        <button className="btn btn-white resumo-compartilhar" onClick={(e) => { e.stopPropagation(); void compartilhar(); }}>
          Compartilhar no WhatsApp
        </button>
      )}
      {aviso && <p>{aviso}</p>}
    </>
  );
}

function cartoesPessoais(r: ResumoPessoal | null, ano: boolean, nome: string, periodo: string): Cartao[] {
  if (!r) {
    return [{ id: "vazio", corpo: (
      <>
        <h1>Você não postou {ano ? `em ${periodo}` : `em ${nome}`}</h1>
        <p>O resumo do grupo está na outra aba.</p>
      </>
    ) }];
  }
  const l: Cartao[] = [];
  l.push({ id: "capa", corpo: (
    <>
      <h1>Seu {ano ? `ano de ${periodo}` : nome}</h1>
      <div className="resumo-grade">
        <Grande n={r.pratos} rotulo={r.pratos === 1 ? "prato" : "pratos"} />
        <Grande n={formatPoints(r.pontos)} rotulo="pontos" />
        {r.posicao > 0 && <Grande n={`${r.posicao}º`} rotulo="no grupo" />}
      </div>
    </>
  ) });
  if (r.ingrediente) l.push({ id: "ingrediente", corpo: (
    <>
      <p className="resumo-pre">O seu ingrediente</p>
      <h1 className="resumo-destaque">{r.ingrediente.nome}</h1>
      <p>Em {r.ingrediente.pratos} dos seus {r.ingrediente.comEstimativa} pratos com estimativa.</p>
    </>
  ) });
  l.push({ id: "casa", corpo: (
    <>
      <p className="resumo-pre">Casa ou rua</p>
      <div className="resumo-grade">
        <Grande n={r.casa} rotulo="em casa" />
        <Grande n={r.rua} rotulo="comprados" />
      </div>
      {r.comPreco > 0 && <p>Você gastou {formatMoney(r.gasto)} nos {r.comPreco} pratos com preço.</p>}
    </>
  ) });
  if (r.melhorPrato) {
    const p = r.melhorPrato;
    l.push({ id: "melhor", corpo: (
      <>
        <p className="resumo-pre">{p.nota != null && p.notas >= 2 ? "Seu prato mais bem avaliado" : "Seu último prato"}</p>
        {p.thumbPath && <div className="resumo-foto"><StoragePhoto path={p.thumbPath} alt="" ratio="1 / 1" /></div>}
        <h2>{p.title || "Sem título"}</h2>
        {p.nota != null && p.notas >= 2 && <p>⭐ {formatRating(p.nota)} ({p.notas} notas)</p>}
      </>
    ) });
  }
  l.push({ id: "missoes", corpo: (
    <>
      <p className="resumo-pre">Suas missões</p>
      {r.missoes.length ? (
        <ol className="resumo-lista">
          {r.missoes.map((m, k) => <li key={`${m.nome}-${k}`}><span>{m.emoji} {m.nome}</span><b>+{formatPoints(m.bonus)}</b></li>)}
        </ol>
      ) : <p>Nenhuma fechada desta vez.</p>}
      {r.quaseLa && (
        <p className="resumo-quase">Quase: {r.quaseLa.emoji} {r.quaseLa.nome} — {r.quaseLa.feitos} de {r.quaseLa.need}.</p>
      )}
    </>
  ) });
  if (r.anterior) {
    const dp = r.pratos - r.anterior.pratos;
    const dpts = Math.round((r.pontos - r.anterior.pontos) * 100) / 100;
    const sinal = (n: number) => (n > 0 ? `+${formatPoints(n)}` : formatPoints(n));
    l.push({ id: "comparacao", corpo: (
      <>
        <p className="resumo-pre">Comparado com {ano ? periodoAnterior(periodo) : nomeDoPeriodo(periodoAnterior(periodo), new Date().getFullYear())}</p>
        <div className="resumo-grade">
          <Grande n={sinal(dp)} rotulo={Math.abs(dp) === 1 ? "prato" : "pratos"} />
          <Grande n={sinal(dpts)} rotulo="pontos" />
        </div>
        <p>{dpts > 0 ? "Mandou bem." : dpts < 0 ? "O próximo é seu." : "Igualzinho."}</p>
      </>
    ) });
  }
  return l;
}
